/**
 * FunctionManagement.jsx
 * 
 * Admin interface for managing organizational functions.
 * Functions represent specific work responsibilities (e.g., Debtors, Creditors, IT Support)
 * that can span across or within departments.
 * 
 * Features:
 * - View all functions with active/inactive filter
 * - Create new functions with name, description, icon, and color
 * - Edit existing function details
 * - Deactivate/reactivate functions
 * - Manage user assignments to functions
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import './FunctionManagement.scss';
import { 
    FaEdit, 
    FaPlus, 
    FaSave, 
    FaSpinner, 
    FaTimes, 
    FaUsers, 
    FaDollarSign, 
    FaFileInvoice,
    FaLaptopCode,
    FaGavel,
    FaCalculator,
    FaBox,
    FaHandshake,
    FaChartLine,
    FaClipboardCheck,
    FaHeadset,
    FaTasks,
    FaSearch
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * Default icon options for functions - using react-icons
 */
const ICON_OPTIONS = [
    { value: 'FaDollarSign', label: 'Debtors/Finance', Icon: FaDollarSign },
    { value: 'FaFileInvoice', label: 'Creditors/Invoices', Icon: FaFileInvoice },
    { value: 'FaLaptopCode', label: 'IT Support', Icon: FaLaptopCode },
    { value: 'FaGavel', label: 'Legal/Compliance', Icon: FaGavel },
    { value: 'FaCalculator', label: 'Payroll', Icon: FaCalculator },
    { value: 'FaBox', label: 'Asset Management', Icon: FaBox },
    { value: 'FaHandshake', label: 'Contracts', Icon: FaHandshake },
    { value: 'FaChartLine', label: 'Reporting', Icon: FaChartLine },
    { value: 'FaClipboardCheck', label: 'Compliance', Icon: FaClipboardCheck },
    { value: 'FaHeadset', label: 'Support', Icon: FaHeadset },
    { value: 'FaTasks', label: 'Tasks', Icon: FaTasks },
    { value: 'FaUsers', label: 'Team', Icon: FaUsers }
];

/**
 * Default color options for functions
 */
const COLOR_OPTIONS = [
    '#3498db', // Blue
    '#2ecc71', // Green
    '#e74c3c', // Red
    '#f39c12', // Orange
    '#9b59b6', // Purple
    '#1abc9c', // Teal
    '#34495e', // Dark Gray
    '#e91e63', // Pink
    '#00bcd4', // Cyan
    '#ff9800'  // Amber
];

/**
 * FunctionManagement Component
 * Main component for managing organizational functions.
 */
const FunctionManagement = () => {
    const { apiFetch, user } = useAuth();
    
    // State for functions list
    const [functions, setFunctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInactive, setShowInactive] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    // State for modal
    const [showModal, setShowModal] = useState(false);
    const [editingFunction, setEditingFunction] = useState(null);
    const [saving, setSaving] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        functionName: '',
        description: '',
        iconClass: 'FaDollarSign',
        color: '#3498db',
        sortOrder: 0
    });
    
    // Validation errors
    const [errors, setErrors] = useState({});

    /**
     * Fetch functions from API
     */
    const fetchFunctions = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiFetch(`/api/Functions?includeInactive=${showInactive}`);
            const data = await response.json();
            
            if (data.success) {
                setFunctions(data.data);
            } else {
                toast.error('Failed to load functions');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching functions:', error);
            toast.error('Error loading functions');
        } finally {
            setLoading(false);
        }
    }, [apiFetch, showInactive]);

    useEffect(() => {
        fetchFunctions();
    }, [fetchFunctions]);

    const visibleFunctions = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const matches = !q
            ? functions
            : functions.filter(func =>
                (func.functionName || '').toLowerCase().includes(q)
                || (func.description || '').toLowerCase().includes(q)
                || (func.iconClass || '').toLowerCase().includes(q)
            );

        return [...matches].sort((a, b) => {
            const sortDiff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
            if (sortDiff !== 0) return sortDiff;
            return (a.functionName || '').localeCompare(b.functionName || '');
        });
    }, [functions, searchQuery]);

    const stats = useMemo(() => ({
        total: functions.length,
        active: functions.filter(f => f.isActive).length,
        inactive: functions.filter(f => !f.isActive).length
    }), [functions]);

    /**
     * Handle form field change
     */
    const handleFieldChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear validation error when field is modified
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    /**
     * Validate form data
     */
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.functionName?.trim()) {
            newErrors.functionName = 'Function name is required';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /**
     * Open modal for creating new function
     */
    const handleCreateNew = () => {
        setEditingFunction(null);
        setFormData({
            functionName: '',
            description: '',
            iconClass: 'FaDollarSign',
            color: '#3498db',
            sortOrder: functions.length + 1
        });
        setErrors({});
        setShowModal(true);
    };

    /**
     * Open modal for editing existing function
     */
    const handleEdit = (func) => {
        setEditingFunction(func);
        setFormData({
            functionName: func.functionName,
            description: func.description || '',
            iconClass: func.iconClass || 'FaDollarSign',
            color: func.color || '#3498db',
            sortOrder: func.sortOrder,
            isActive: func.isActive
        });
        setErrors({});
        setShowModal(true);
    };

    /**
     * Close modal and reset state
     */
    const handleCloseModal = () => {
        setShowModal(false);
        setEditingFunction(null);
        setFormData({
            functionName: '',
            description: '',
            iconClass: 'FaDollarSign',
            color: '#3498db',
            sortOrder: 0
        });
        setErrors({});
    };

    /**
     * Save function (create or update)
     */
    const handleSave = async () => {
        if (!validateForm()) return;
        
        setSaving(true);
        try {
            const payload = editingFunction ? {
                functionName: formData.functionName,
                description: formData.description,
                iconClass: formData.iconClass,
                color: formData.color,
                sortOrder: formData.sortOrder,
                isActive: formData.isActive,
                modifiedByUserID: user.userID
            } : {
                functionName: formData.functionName,
                description: formData.description,
                iconClass: formData.iconClass,
                color: formData.color,
                sortOrder: formData.sortOrder,
                createdByUserID: user.userID
            };

            let response;
            if (editingFunction) {
                // Update existing function
                response = await apiFetch(`/api/Functions/${editingFunction.functionID}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            } else {
                // Create new function
                response = await apiFetch('/api/Functions', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            }

            const data = await response.json();

            if (data.success) {
                toast.success(data.message || (editingFunction ? 'Function updated' : 'Function created'));
                handleCloseModal();
                fetchFunctions();
            } else {
                toast.error(data.message || 'Failed to save function');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving function:', error);
            toast.error('Error saving function');
        } finally {
            setSaving(false);
        }
    };

    /**
     * Toggle function active status
     */
    const handleToggleActive = async (func) => {
        try {
            const response = await apiFetch(`/api/Functions/${func.functionID}`, {
                method: 'DELETE'
            });

            const data = await response.json();

            if (data.success) {
                toast.success(data.message);
                fetchFunctions();
            } else {
                toast.error(data.message || 'Failed to update function');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error toggling function status:', error);
            toast.error('Error updating function');
        }
    };

    return (
        <div className="function-management-page">
<div className="fmp-page-header">
                <div className="fmp-header-left">
                    <h1>Function Management</h1>
                    <p className="fmp-page-description">
                        Manage work functions (e.g., Debtors, Creditors, IT Support, Compliance). Functions represent specific work responsibilities that can span across departments. They are used to route and categorize action items, and can be assigned as defaults in email mailbox configurations.
                    </p>
                </div>
                <button className="fmp-btn-primary" onClick={handleCreateNew}>
                    <FaPlus /> Create Function
                </button>
            </div>

            <div className="fmp-overview-panel">
                <div className="fmp-stats-row">
                    <div className="fmp-stat-card">
                        <span className="fmp-stat-value">{stats.total}</span>
                        <span className="fmp-stat-label">Total</span>
                    </div>
                    <div className="fmp-stat-card">
                        <span className="fmp-stat-value fmp-active">{stats.active}</span>
                        <span className="fmp-stat-label">Active</span>
                    </div>
                    <div className="fmp-stat-card">
                        <span className="fmp-stat-value fmp-inactive">{stats.inactive}</span>
                        <span className="fmp-stat-label">Inactive</span>
                    </div>
                </div>

                <div className="fmp-toolbar">
                    <div className="fmp-search-wrap">
                        <FaSearch className="fmp-search-icon" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search functions..."
                            aria-label="Search functions"
                        />
                    </div>

                    <label className="fmp-checkbox-label">
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                        />
                        Show inactive functions
                    </label>

                    <span className="fmp-count">{visibleFunctions.length} shown</span>
                </div>
            </div>

            {loading ? (
                <div className="fmp-loading-state">
                    <FaSpinner className="spinner" />
                    <p>Loading functions...</p>
                </div>
            ) : (
                <div className="fmp-grid">
                    {visibleFunctions.length === 0 ? (
                        <div className="fmp-empty-state">
                            <p>No functions found for the current filters.</p>
                            <button className="fmp-btn-primary" onClick={handleCreateNew}>
                                <FaPlus /> Create Function
                            </button>
                        </div>
                    ) : (
                        visibleFunctions.map(func => {
                            const iconOption = ICON_OPTIONS.find(opt => opt.value === func.iconClass);
                            const IconComponent = iconOption ? iconOption.Icon : FaDollarSign;
                            
                            return (
                                <div key={func.functionID} className={`fmp-card ${!func.isActive ? 'inactive' : ''}`}>
                                    <div className="fmp-card-header" style={{ borderLeftColor: func.color }}>
                                        <div className="fmp-icon-wrapper" style={{ backgroundColor: func.color }}>
                                            <IconComponent />
                                        </div>
                                        <div className="fmp-header-content">
                                            <h3>{func.functionName}</h3>
                                            {!func.isActive && <span className="fmp-inactive-badge">Inactive</span>}
                                        </div>
                                    </div>
                                    
                                    <div className="fmp-card-body">
                                        <p className="fmp-description">{func.description || 'No description provided'}</p>
                                        <span className="fmp-order-pill">Sort order: {func.sortOrder ?? 0}</span>
                                    </div>
                                    
                                    <div className="fmp-card-actions">
                                        <button 
                                            className="fmp-btn-secondary" 
                                            onClick={() => handleEdit(func)}
                                        >
                                            <FaEdit /> Edit
                                        </button>
                                        <button 
                                            className={`fmp-btn-toggle ${func.isActive ? 'danger' : 'success'}`}
                                            onClick={() => handleToggleActive(func)}
                                        >
                                            {func.isActive ? 'Deactivate' : 'Reactivate'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fmp-modal-overlay">
                    <div className="fmp-modal-content">
                        <div className="fmp-modal-header">
                            <h2>{editingFunction ? 'Edit Function' : 'Create Function'}</h2>
                            <button className="fmp-close-btn" onClick={handleCloseModal}>
                                <FaTimes />
                            </button>
                        </div>
                        
                        <div className="fmp-modal-body">
                            <div className="fmp-form-group">
                                <label htmlFor="functionName">Function Name *</label>
                                <input
                                    type="text"
                                    id="functionName"
                                    value={formData.functionName}
                                    onChange={(e) => handleFieldChange('functionName', e.target.value)}
                                    className={errors.functionName ? 'error' : ''}
                                    placeholder="e.g., Debtors, Creditors, IT Support"
                                />
                                {errors.functionName && <span className="fmp-error-message">{errors.functionName}</span>}
                            </div>

                            <div className="fmp-form-group">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => handleFieldChange('description', e.target.value)}
                                    rows="3"
                                    placeholder="Describe the function's responsibilities..."
                                />
                            </div>

                            <div className="fmp-form-row">
                                <div className="fmp-form-group">
                                    <label htmlFor="iconClass">Icon</label>
                                    <select
                                        id="iconClass"
                                        value={formData.iconClass}
                                        onChange={(e) => handleFieldChange('iconClass', e.target.value)}
                                    >
                                        {ICON_OPTIONS.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="fmp-icon-preview">
                                        {(() => {
                                            const selectedOption = ICON_OPTIONS.find(opt => opt.value === formData.iconClass);
                                            const IconComponent = selectedOption ? selectedOption.Icon : FaDollarSign;
                                            return <IconComponent />;
                                        })()}
                                    </div>
                                </div>

                                <div className="fmp-form-group">
                                    <label htmlFor="sortOrder">Sort Order</label>
                                    <input
                                        type="number"
                                        id="sortOrder"
                                        value={formData.sortOrder}
                                        onChange={(e) => handleFieldChange('sortOrder', parseInt(e.target.value) || 0)}
                                        min="0"
                                    />
                                </div>
                            </div>

                            <div className="fmp-form-group">
                                <label htmlFor="color">Color</label>
                                <div className="fmp-color-picker">
                                    {COLOR_OPTIONS.map(color => (
                                        <div
                                            key={color}
                                            className={`fmp-color-option ${formData.color === color ? 'selected' : ''}`}
                                            style={{ backgroundColor: color }}
                                            onClick={() => handleFieldChange('color', color)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        <div className="fmp-modal-footer">
                            <button className="fmp-btn-secondary" onClick={handleCloseModal} disabled={saving}>
                                Cancel
                            </button>
                            <button className="fmp-btn-primary" onClick={handleSave} disabled={saving}>
                                {saving ? (
                                    <>
                                        <FaSpinner className="spinner" /> Saving...
                                    </>
                                ) : (
                                    <>
                                        <FaSave /> Save Function
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FunctionManagement;
